// turf
import { centroid, bearing, transformRotate } from '@turf/turf';

// mapbox lib
import EventEmitter from 'events';

// mapbox
const emitter = new EventEmitter();

let startAngle: number, endAngle: number, oldAngle: number;

const drawRotateMode: any = {
    rotatestart: (featCollection, originalCenter) => {},
    rotating: (featCollection, originalCenter, lastMouseDown) => {},
    rotateend: (featCollection) => {},

    emitListener: function (state: any): void {
        emitter.addListener(
            'rotatestart',
            function (this: any) {
                this.rotatestart(state.featCollection, state.originalCenter);
            }.bind(this),
        );

        emitter.addListener(
            'rotating',
            function (this: any) {
                this.rotating(state.featCollection, state.originalCenter);
            }.bind(this),
        );

        emitter.addListener(
            'rotateend',
            function (this: any) {
                this.rotateend(state.featCollection);
            }.bind(this),
        );
    },

    initGeomAngle: function (state: any): number {
        let angle = state.featCollection.features[0].properties.rotation;
        return angle ? angle : 0;
    },

    initFeatureCollection: function ({ selectedFeatures }, state: any): void {
        selectedFeatures.forEach((feature: any) => {
            state.featCollection.features.push(this._ctx.api.get(feature));

            state.initialSelectedFeatureGeom.push(
                JSON.parse(JSON.stringify(this._ctx.api.get(feature))),
            );
        });

        oldAngle = this.initGeomAngle(state);
    },

    generateMultiGeom: function (state: any): void {
        let geomType = state.initialSelectedFeatureGeom[0].geometry.type;

        state.multiGeom = {
            type: 'Feature',
            id: 1,
            geometry: {
                type: 'Multi' + geomType,
                coordinates: geomType === 'Point' ? [] : [[]],
            },
            properties: {
                rotation: 0,
            },
        };

        state.initialMultiGeom = {
            type: 'Feature',
            id: 2,
            geometry: {
                type: 'Multi' + geomType,
                coordinates: geomType === 'Point' ? [] : [[]],
            },
            properties: {
                rotation: 0,
            },
        };

        state.initialSelectedFeatureGeom.forEach((x) => {
            state.initialMultiGeom.geometry.coordinates.push(
                x.geometry.coordinates,
            );
            state.multiGeom.geometry.coordinates.push(x.geometry.coordinates);
        });
    },

    onSetup: function (opts: any): typeof state {
        let state: any = {
            mode: 'rotate',
            isRotateFromAxis: opts.isRotateFromAxis,
            startCoordinates: [],
            initialSelectedFeatureGeom: [],
            originalCenter: {},
            multiGeom: {},
            initialMultiGeom: {},
            featCollection: { type: 'FeatureCollection', features: [] },
        };

        this.emitListener(state);
        this.initFeatureCollection(opts, state);

        if (!state.isRotateFromAxis) this.generateMultiGeom(state);

        return state;
    },

    onMouseDown: function (state: any, e: any): typeof state {
        e.target['dragPan'].disable();

        if (!state.isRotateFromAxis) {
            state.multiGeom.geometry.coordinates =
                state.initialMultiGeom.geometry.coordinates;
        } else {
            state.featCollection.features.forEach((feat: any) => {
                state.initialSelectedFeatureGeom.forEach(
                    (initialFeature: any) => {
                        if (feat.id !== initialFeature.id) return;

                        feat.geometry.coordinates =
                            initialFeature.geometry.coordinates;
                    },
                );
            });
        }

        state.originalCenter = !state.isRotateFromAxis
            ? centroid(state.initialMultiGeom.geometry)
            : centroid(state.featCollection);

        state.startCoordinates = [e.lngLat.lng, e.lngLat.lat];

        startAngle = bearing(state.originalCenter, [
            e.lngLat.lng,
            e.lngLat.lat,
        ]);

        return state;
    },

    onMouseUp: function (state: any, e: any): typeof state {
        e.target['dragPan'].enable();

        emitter.emit('rotateend');

        state.originalCenter = false;

        oldAngle = !state.isRotateFromAxis
            ? state.initialMultiGeom.properties.rotation
            : state.initialSelectedFeatureGeom[0].properties.rotation;

        return state;
    },

    destructMultiGeom: function (state: any): void {
        state.featCollection.features.forEach((element: any, i: number) => {
            this._ctx.api.add({
                type: 'Feature',
                id: element.id,
                geometry: {
                    type: element.geometry.type,
                    coordinates: state.initialMultiGeom.geometry.coordinates[i],
                },
                properties: {
                    rotation: state.initialMultiGeom.properties.rotation,
                },
            });
        });
    },

    updateRotationAngle: function (): number {
        const finalAngle = Math.round(this.calculateAngle() * 10) / 10;

        return (oldAngle + finalAngle + 360) % 360;
    },

    updateMultiGeomFeature: function (state: any, angle: number): void {
        state.initialMultiGeom.geometry = transformRotate(
            state.multiGeom.geometry,
            angle,
        );

        state.initialMultiGeom.properties.rotation = this.updateRotationAngle();

        state.initialMultiGeom.geometry.coordinates.forEach(
            (element: any, i: number) => {
                if (element.length !== 0) return;

                state.initialMultiGeom.geometry.coordinates.splice(i, 1);
            },
        );

        this.destructMultiGeom(state);
    },

    updateIndividualFeatures: function (state: any, angle: number): void {
        state.initialSelectedFeatureGeom.forEach((initialFeature: any) => {
            state.featCollection.features.forEach((feature: any) => {
                if (feature.id !== initialFeature.id) return;

                initialFeature.geometry = transformRotate(
                    feature.geometry,
                    angle,
                );
            });

            initialFeature.properties.rotation = this.updateRotationAngle();

            this._ctx.api.add(initialFeature);
        });
    },

    updateFeatures: function (state: any, angle: number): void {
        if (!state.isRotateFromAxis) {
            this.updateMultiGeomFeature(state, angle);

            return;
        }

        this.updateIndividualFeatures(state, angle);
    },

    onDrag: function (state: any, e: any): void {
        if (state.mode !== 'rotate') return;

        emitter.emit('rotating');

        endAngle = bearing(state.originalCenter, [e.lngLat.lng, e.lngLat.lat]);

        this.updateFeatures(state, this.calculateDraggedAngle(state, e));
    },

    toDisplayFeatures: function (state: any, geojson: any, display: any): void {
        display(geojson);
    },

    calculateDraggedAngle: function (state: any, e: any): number {
        return (
            Math.round(
                (bearing(state.originalCenter, [e.lngLat.lng, e.lngLat.lat]) -
                    bearing(state.originalCenter, state.startCoordinates)) *
                    10,
            ) / 10
        );
    },

    calculateAngle: function (): number {
        return (endAngle - startAngle + 360) % 360;
    },

};

export default drawRotateMode;
